import { type FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueGetterParams
} from '@mui/x-data-grid';
// custom
import SimpleSpinner from '../loaders/spinners/SimpleSpinner';
import { IPromotion } from '../../common/state/promotions/promotion';


const ContainerStyled = styled.section`
    width: 100%;
    min-height: 100px;
    max-height: 600px;
    table {
        width: 100%
    }
`;

type IPromotionOverviewTableProps = {
  list: IPromotion[] | undefined
};

type IPromotionTableProps = Omit<IPromotion, 'id'> & { 'id': number };

type MuiTableProps = {
  list: IPromotionTableProps[];
};

// headers for export to csv
const CustomToolbar: FC = () => {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', backgroundColor: '#f5ebe0', '& .MuiButton-root, & .MuiButtonBase-root': { color: 'black' } }}>
      <GridToolbarColumnsButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarFilterButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

const MuiTableAdmin: FC<MuiTableProps> = ({ list }) => {

  // material columns
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 10, headerClassName: 'custom-header' },
    {
      field: 'promo_code',
      headerName: 'Promo Code',
      headerClassName: 'custom-header',
      sortable: true,
      flex: 1
    },
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      headerClassName: 'custom-header',
      flex: 1
    },
    {
      field: 'discount',
      headerName: 'Discount',
      sortable: true,
      headerClassName: 'custom-header',
      width: 100
    },

    {
      field: 'start_date',
      headerName: 'Start Date',
      type: 'date',
      headerClassName: 'custom-header',
      width: 200,
      sortable: true,
      valueGetter: (data: GridValueGetterParams) => {
        return new Date(data.row.start_date);
      }
    },
    {
      field: 'end_date',
      type: 'date',
      headerName: 'End Date',
      headerClassName: 'custom-header',
      sortable: true,
      width: 200,
      valueGetter: (data: GridValueGetterParams) => {
        return new Date(data.row.end_date);
      }
    },
  ];

  return (<Box sx={{ minHeight: 200, width: '100%' }}>
    <DataGrid
      style={{ fontSize: 15 }}
      rows={list}
      columns={columns}
      components={{ Toolbar: CustomToolbar }}
      sx={{
        '.custom-header': {
          backgroundColor: '#e3d5ca',
          fontWeight: 600,
          color: 'black',
        },
      }}
    />
  </Box>
  );
};


const PromotionOverviewMuiTableAdmin: FC<IPromotionOverviewTableProps> = ({ list }) => {
  // states
  const [title, setTitle] = useState<string>('');
  const [subTitle, setSubTitle] = useState<string>('Please wait...');
  const [promoTableList, setPromoTableList] = useState<IPromotionTableProps[]>([]);


  // lifecycle
  useEffect(() => {
    if (list && list.length > 0) {
      const tableList: IPromotionTableProps[] = list.map((item: IPromotion, idx: number) => ({ ...item, id: idx + 1 }));
      setPromoTableList(tableList);
      setTitle('Promotion Offers Detail');
      setSubTitle('Promotion records breakdown with their corresponding discount...');
    } else if (list && list.length === 0) {
      setTitle('No Promotion offers');
      setSubTitle('There are currently no promotion offers available. Click on + to create a promotion');
    }
  }, [list]);
  return <ContainerStyled
    className="mt-2 relative bg-gray-100 dark:bg-gray-900 rounded-lg 
                     flex flex-col justify-start items-start shadow-lg 
                    p-4 overflow-hidden overflow-scroll">
    <h2 className="text-gray-700 dark:text-teal-400 text-2xl font-medium mb-2">{title}</h2>
    <p className="text-gray-700 dark:text-gray-300 dartext-base mb-4">
      {subTitle}
    </p>
    <SimpleSpinner display={`inline-block`} loading={!list} title={'Loading promotion...'} />
    {Boolean(list) && (list as IPromotion[]).length > 0 && <MuiTableAdmin list={promoTableList} />}
  </ContainerStyled>;
};

export default PromotionOverviewMuiTableAdmin;
